import React, { useState } from 'react'
import { FlipHorizontal, FlipVertical, Play, Pause, Volume2, VolumeX } from 'lucide-react'
import FitToPageButton from './FitToPageButton'
import MediaPreview from './MediaPreview'

/**
 * Video Element Properties Component
 * Handles the properties panel for video elements including transparency, brightness, contrast, intensity, and flip options
 */
const VideoElementProperties = ({
  element,
  selectedElement,
  updateElementProperty,
  currentPage
}) => {
  if (element.type !== 'video') {
    return null
  }

  return (
    <div className="space-y-4">
      {/* Media Preview — play inline or view full size */}
      <MediaPreview element={element} />

      {/* Video Adjustments Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Video Adjustments</h3>
        <div className="space-y-3">
          {/* Transparency */}
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-gray-600 font-medium">Transparency</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={element.opacity ?? 1}
                onChange={(e) => updateElementProperty(selectedElement, 'opacity', parseFloat(e.target.value))}
                className="w-24"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{Math.round((element.opacity ?? 1) * 100)}%</span>
            </div>
          </div>

          {/* Brightness */}
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-gray-600 font-medium">Brightness</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-100"
                max="100"
                value={element.brightness ?? 0}
                onChange={(e) => updateElementProperty(selectedElement, 'brightness', parseInt(e.target.value))}
                className="w-24"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{element.brightness ?? 0}</span>
            </div>
          </div>

          {/* Contrast */}
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-gray-600 font-medium">Contrast</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-100"
                max="100"
                value={element.contrast ?? 0}
                onChange={(e) => updateElementProperty(selectedElement, 'contrast', parseInt(e.target.value))}
                className="w-24"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{element.contrast ?? 0}</span>
            </div>
          </div>

          {/* Intensity (Saturation) */}
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-gray-600 font-medium">Intensity</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-100"
                max="100"
                value={element.intensity ?? 0}
                onChange={(e) => updateElementProperty(selectedElement, 'intensity', parseInt(e.target.value))}
                className="w-24"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{element.intensity ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Video Controls Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Video Controls</h3>
        <div className="space-y-3">
          {/* Muted */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600">Muted</label>
            <input
              type="checkbox"
              checked={element.muted !== false}
              onChange={(e) => updateElementProperty(selectedElement, 'muted', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>

          {/* Loop */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600">Loop</label>
            <input
              type="checkbox"
              checked={element.loop || false}
              onChange={(e) => updateElementProperty(selectedElement, 'loop', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>

          {/* Auto Play */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600">Auto Play</label>
            <input
              type="checkbox"
              checked={element.autoPlay || false}
              onChange={(e) => updateElementProperty(selectedElement, 'autoPlay', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Dimensions & Properties Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Dimensions & Properties</h3>
        <div className="grid grid-cols-2 gap-3">
          {/* X Position */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">X</label>
            <input
              type="number"
              value={Math.round(element.x || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'x', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Y Position */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Y</label>
            <input
              type="number"
              value={Math.round(element.y || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'y', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Width */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Width</label>
            <input
              type="number"
              value={element.width || 100}
              onChange={(e) => updateElementProperty(selectedElement, 'width', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="10"
              max="2000"
            />
          </div>

          {/* Height */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Height</label>
            <input
              type="number"
              value={element.height || 100}
              onChange={(e) => updateElementProperty(selectedElement, 'height', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="10"
              max="2000"
            />
          </div>

          {/* Border Radius */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Radius</label>
            <input
              type="number"
              value={element.borderRadius || 0}
              onChange={(e) => updateElementProperty(selectedElement, 'borderRadius', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
              max="100"
            />
          </div>

          {/* Rotation */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Rotation</label>
            <input
              type="number"
              value={Math.round(element.rotation || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'rotation', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="-360"
              max="360"
            />
          </div>

          {/* Layer */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Layer</label>
            <input
              type="number"
              value={element.layer || 0}
              onChange={(e) => updateElementProperty(selectedElement, 'layer', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
              max="1000"
            />
          </div>
        </div>

        {/* Advanced Properties Section */}
        <div className="grid grid-cols-2 gap-3">
          {/* Blend Mode */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Blend</label>
            <select
              value={element.blendMode || 'normal'}
              onChange={(e) => updateElementProperty(selectedElement, 'blendMode', e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="normal">Normal</option>
              <option value="multiply">Multiply</option>
              <option value="screen">Screen</option>
              <option value="overlay">Overlay</option>
              <option value="darken">Darken</option>
              <option value="lighten">Lighten</option>
              <option value="color-dodge">Color Dodge</option>
              <option value="color-burn">Color Burn</option>
              <option value="hard-light">Hard Light</option>
              <option value="soft-light">Soft Light</option>
              <option value="difference">Difference</option>
              <option value="exclusion">Exclusion</option>
            </select>
          </div>

          {/* Object Fit */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Fit</label>
            <select
              value={element.objectFit || 'fill'}
              onChange={(e) => updateElementProperty(selectedElement, 'objectFit', e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="fill">Fill (Stretch)</option>
              <option value="cover">Cover</option>
              <option value="contain">Contain</option>
              <option value="none">None</option>
              <option value="scale-down">Scale Down</option>
            </select>
          </div>
        </div>
      </div>

      {/* Transform Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Transform</h3>
        <div className="space-y-3">
          {/* Flip Controls */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600">Flip</label>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => updateElementProperty(selectedElement, 'flipX', !element.flipX)}
                className={`px-3 py-2 border rounded-md transition-colors ${
                  element.flipX
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
                title="Flip Horizontal"
              >
                <FlipHorizontal className="w-4 h-4" />
              </button>
              <button
                onClick={() => updateElementProperty(selectedElement, 'flipY', !element.flipY)}
                className={`px-3 py-2 border rounded-md transition-colors ${
                  element.flipY
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
                title="Flip Vertical"
              >
                <FlipVertical className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Scale Controls */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600 min-w-[50px]">Scale X</label>
              <input
                type="number"
                step="0.1"
                value={element.scaleX || 1}
                onChange={(e) => updateElementProperty(selectedElement, 'scaleX', parseFloat(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0.1"
                max="5"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600 min-w-[50px]">Scale Y</label>
              <input
                type="number"
                step="0.1"
                value={element.scaleY || 1}
                onChange={(e) => updateElementProperty(selectedElement, 'scaleY', parseFloat(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0.1"
                max="5"
              />
            </div>
          </div>
        </div>

        {/* Fit to Page Button */}
        <div className="mt-3">
          <FitToPageButton
            selectedElement={selectedElement}
            updateElementProperty={updateElementProperty}
            currentPage={currentPage}
            element={element}
          />
        </div>
      </div>
    </div>
  )
}

export default VideoElementProperties
